// @file The store for the AuthLoginApp
import { __ } from '@@/bits/intl'
import { navigateTo, transformCurrentUrl } from '@@/bits/location'
import { addQueryParams } from '@@/bits/url'
import { HttpCode } from '@@/enums'
import { fetchJson, HTTPMethod } from '@padlet/fetch'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
interface FormJsonResponse {
  redirect_to: string
}

export const useAuthOrgVerificationStore = defineStore('authAuthOrgVerificationStore', () => {
  function initialize(startingState): void {
    codeChallenge.value = startingState?.codeChallenge
    originAppClientId.value = startingState?.originAppClientId
    pkceState.value = startingState?.pkceState
    originAppRedirectUri.value = startingState?.originAppRedirectUri
    codeChallengeMethod.value = startingState?.codeChallengeMethod
  }

  // State
  const orgDomain = ref('')
  const orgDomainValidationMessage = ref('')
  const isAwaitingServerResponse = ref(false)
  const codeChallenge = ref<string | null>(null)
  const originAppClientId = ref<string | null>(null)
  const pkceState = ref<string | null>(null)
  const originAppRedirectUri = ref<string | null>(null)
  const codeChallengeMethod = ref<string | null>(null)

  // Getters
  const isPkceLogin = computed(
    () =>
      codeChallenge.value != null &&
      codeChallengeMethod.value != null &&
      pkceState.value != null &&
      originAppClientId.value != null &&
      originAppRedirectUri.value != null,
  )

  // Actions
  function setOrgDomain(domain: string): void {
    orgDomain.value = domain
  }

  function setOrgDomainValidationMessage(message: string): void {
    orgDomainValidationMessage.value = message
  }

  async function verifyOrgDomain(): Promise<void> {
    if (orgDomain.value === '') {
      setOrgDomainValidationMessage(__('Please fill out this field.'))
      return
    }

    isAwaitingServerResponse.value = true

    try {
      const url = transformCurrentUrl(
        {},
        {
          path: '/api/auth/org_domain_verify',
          search: { subdomain: orgDomain.value },
        },
      )
      const response: FormJsonResponse = await fetchJson(url, {
        method: HTTPMethod.get,
      })
      const loginUrl = response.redirect_to
      if (isPkceLogin.value) {
        const pkceLoginUrl = addQueryParams(
          loginUrl,
          `code_challenge=${codeChallenge.value as string}&code_challenge_method=${
            codeChallengeMethod.value as string
          }&redirect_uri=${encodeURIComponent(originAppRedirectUri.value as string)}&client_id=${
            originAppClientId.value as string
          }&pkce_state=${pkceState.value as string}`,
        )
        navigateTo(pkceLoginUrl)
      } else {
        navigateTo(loginUrl)
      }
    } catch (e) {
      if (e.status === HttpCode.UnprocessableEntity) {
        const parsedErrorMessage = JSON.parse(e.message)
        setOrgDomainValidationMessage(parsedErrorMessage.error)
      } else if (e.status === HttpCode.RateLimitError) {
        setOrgDomainValidationMessage(__('Our servers are overloaded. Please try again in a minute.'))
      } else if (e.status >= HttpCode.RateLimitError) {
        setOrgDomainValidationMessage(__('Looks like our servers are down. Please try again in a bit.'))
      }
    } finally {
      isAwaitingServerResponse.value = false
    }
  }

  return {
    // State
    orgDomain,
    orgDomainValidationMessage,
    isAwaitingServerResponse,
    codeChallenge,
    originAppClientId,
    pkceState,
    originAppRedirectUri,
    codeChallengeMethod,

    // Getters
    isPkceLogin,

    // Actions
    setOrgDomain,
    setOrgDomainValidationMessage,
    verifyOrgDomain,
    initialize,
  }
})
